/**
 * variant-swatches.js (v2.4.1)
 * PDP: single product mount (#variant-swatches-root)
 * Collection cards: batch scan ([data-variant-swatches-root])
 */
(function () {
  "use strict";

  if (window.__variantSwatches) {
    window.__variantSwatches.initProduct();
    window.__variantSwatches.scanCards();
    return;
  }

  var VERSION = "2.4.8";
  var COLLAPSED_LIMIT = 4;
  var IMAGE_WIDTH = 500;
  var requestCache =
    window.__variantSwatchesRequestCache ||
    (window.__variantSwatchesRequestCache = {});

  var cardsInFlight = false;
  var cardsPendingRescan = false;

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function isProductPage() {
    return document.body && document.body.classList.contains("template-product");
  }

  function stopEvent(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  function normalizeGroup(raw) {
    if (!raw) return null;
    if (raw.group) return normalizeGroup(raw.group);
    if (raw.products && raw.products.length) return raw;
    return null;
  }

  function getTotalCount(group) {
    return group.product_count || group.products.length;
  }

  function productKey(product) {
    return String(product.product_id || product.handle || product.option_value);
  }

  function normalizeColorName(value) {
    return String(value || "")
      .toLowerCase()
      .trim()
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ");
  }

  function colorSlug(value) {
    return normalizeColorName(value).replace(/\s+/g, "-");
  }

  function findProductByColor(products, preferredColor) {
    if (!preferredColor || !products || !products.length) return null;
    var wanted = normalizeColorName(preferredColor);
    var wantedSlug = colorSlug(preferredColor);

    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      var candidates = [
        product.option_value,
        product.color,
        product.color_name,
        product.name,
        product.title,
      ];
      for (var c = 0; c < candidates.length; c++) {
        var normalized = normalizeColorName(candidates[c]);
        if (!normalized) continue;
        if (normalized === wanted || colorSlug(candidates[c]) === wantedSlug) {
          return product;
        }
      }
    }
    return null;
  }

  function findProductByHandle(products, preferredHandle) {
    if (!preferredHandle || !products || !products.length) return null;
    var wanted = String(preferredHandle).toLowerCase();
    for (var i = 0; i < products.length; i++) {
      if (String(products[i].handle || "").toLowerCase() === wanted) {
        return products[i];
      }
    }
    return null;
  }

  function getPreferredSelection(mount) {
    return {
      handle: mount.getAttribute("data-selected-handle") || "",
      color: mount.getAttribute("data-selected-color") || "",
    };
  }

  function orderProductsWithSelectedFirst(products, selectedKey) {
    if (!products || !products.length) return [];
    if (!selectedKey) return products.slice();

    var selected = null;
    var rest = [];
    for (var i = 0; i < products.length; i++) {
      if (productKey(products[i]) === selectedKey) {
        selected = products[i];
      } else {
        rest.push(products[i]);
      }
    }

    if (!selected) return products.slice();
    return [selected].concat(rest);
  }

  function pickVisibleProducts(products, limit, selectedKey) {
    return orderProductsWithSelectedFirst(products, selectedKey).slice(0, limit);
  }

  function isFeaturedSlideshowMount(mount) {
    if (!mount || !mount.closest) return false;
    return !!mount.closest(
      ".wt-featured-collection:not(.wt-featured-collection--recommendation) slideshow-section"
    );
  }

  function getCollapsedLimit(mount) {
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 899px)").matches &&
      isFeaturedSlideshowMount(mount)
    ) {
      return 3;
    }
    return COLLAPSED_LIMIT;
  }

  function ensureSelectedKey(mount, products) {
    var preferred = getPreferredSelection(mount);
    var preferredHandle = preferred.handle;
    var preferredColor = preferred.color;
    var existingKey = mount.dataset.vsSelectedKey || "";

    if (preferredColor) {
      var byColor = findProductByColor(products, preferredColor);
      if (byColor) {
        mount.dataset.vsSelectedKey = productKey(byColor);
        return;
      }
    }

    if (existingKey) return;

    if (preferredHandle) {
      var byHandle = findProductByHandle(products, preferredHandle);
      if (byHandle) {
        mount.dataset.vsSelectedKey = productKey(byHandle);
        return;
      }
    }

    for (var i = 0; i < products.length; i++) {
      if (products[i].active) {
        mount.dataset.vsSelectedKey = productKey(products[i]);
        return;
      }
    }
  }

  function isProductSelected(product, selectedKey) {
    if (selectedKey) return productKey(product) === selectedKey;
    return !!product.active;
  }

  function getMountConfig(el) {
    return {
      productId: el.getAttribute("data-product-id") || "",
      apiBase: el.getAttribute("data-api-base") || "",
      shop: el.getAttribute("data-shop") || "",
    };
  }

  function cacheKey(cfg, type) {
    return [type, cfg.apiBase, cfg.shop, cfg.productId].join("|");
  }

  function fetchVariantGroupsRaw(cfg) {
    var key = cacheKey(cfg, "single");
    if (!requestCache[key]) {
      var url =
        cfg.apiBase +
        "/variant-groups?productId=" +
        encodeURIComponent(cfg.productId) +
        "&shop=" +
        encodeURIComponent(cfg.shop);

      requestCache[key] = fetch(url, { credentials: "omit" })
        .then(function (res) {
          if (!res.ok) throw new Error("variant-groups request failed: " + res.status);
          return res.json();
        })
        .catch(function (err) {
          delete requestCache[key];
          throw err;
        });
    }
    return requestCache[key];
  }

  function fetchSingleGroup(cfg) {
    return fetchVariantGroupsRaw(cfg).then(normalizeGroup);
  }

  function getMaterialItems(materials) {
    if (!materials || !materials.items || !materials.items.length) return [];
    return materials.items;
  }

  function buildDot(product) {
    var dot = document.createElement("span");
    dot.className = "vs-swatch__dot";
    dot.style.backgroundColor = product.color_hex || "#ccc";
    return dot;
  }

  function getColorLabel(product) {
    return String(
      (product && (product.option_value || product.color_name || product.color || product.name)) ||
        ""
    ).trim();
  }

  function colorMatchesQuery(label, query) {
    var text = normalizeColorName(label);
    var q = normalizeColorName(query);
    if (!q) return true;
    if (text.indexOf(q) !== -1) return true;
    var parts = q.split(" ").filter(Boolean);
    if (parts.length > 1) {
      return parts.every(function (part) {
        return text.indexOf(part) !== -1;
      });
    }
    var words = text.split(" ");
    for (var i = 0; i < words.length; i++) {
      if (words[i].indexOf(q) === 0) return true;
    }
    return false;
  }

  function createWrapper(group) {
    var wrapper = document.createElement("div");
    wrapper.className = "vs-swatch-group";
    wrapper.setAttribute("role", "group");
    wrapper.setAttribute("aria-label", group.name || "Color options");
    wrapper.setAttribute("data-vs-version", VERSION);

    var swatchRow = document.createElement("div");
    swatchRow.className = "vs-swatch-group__swatches";
    wrapper.appendChild(swatchRow);

    return { wrapper: wrapper, swatchRow: swatchRow };
  }

  function syncSwatchFilter(swatchRow, query) {
    if (!swatchRow) return;
    var hasQuery = !!normalizeColorName(query);
    var firstMatch = null;
    swatchRow.querySelectorAll(".vs-swatch").forEach(function (swatch) {
      var label = swatch.getAttribute("data-vs-color") || swatch.getAttribute("aria-label") || "";
      var match = colorMatchesQuery(label, query);
      swatch.classList.toggle("vs-swatch--filtered-out", hasQuery && !match);
      swatch.classList.toggle("vs-swatch--search-hit", hasQuery && match);
      if (hasQuery && match && !firstMatch) firstMatch = swatch;
    });
    if (firstMatch) {
      firstMatch.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
    }
  }

  function clearSwatchFilter(swatchRow) {
    if (!swatchRow) return;
    swatchRow.querySelectorAll(".vs-swatch").forEach(function (swatch) {
      swatch.classList.remove("vs-swatch--filtered-out", "vs-swatch--search-hit");
    });
  }

  function buildColorSearch(products, swatchRow) {
    var active =
      products.filter(function (p) {
        return p.active;
      })[0] || products[0];
    var root = document.createElement("div");
    root.className = "vs-color-search";
    root.setAttribute("data-open", "false");

    var trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "vs-color-search__trigger";
    trigger.setAttribute("aria-expanded", "false");
    trigger.setAttribute("aria-haspopup", "listbox");

    var triggerSwatch = document.createElement("span");
    triggerSwatch.className = "vs-color-search__swatch";
    var triggerDot = document.createElement("span");
    triggerDot.className = "vs-color-search__dot";
    triggerDot.style.backgroundColor = (active && active.color_hex) || "#ccc";
    triggerSwatch.appendChild(triggerDot);

    var triggerName = document.createElement("span");
    triggerName.className = "vs-color-search__name";
    triggerName.textContent = getColorLabel(active) || "Color";

    var chevron = document.createElement("span");
    chevron.className = "vs-color-search__chevron";
    chevron.setAttribute("aria-hidden", "true");
    chevron.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" width="12" height="12" focusable="false">' +
      '<path fill="currentColor" d="M2.2 4.2 6 8l3.8-3.8H2.2z"/>' +
      "</svg>";

    trigger.appendChild(triggerSwatch);
    trigger.appendChild(triggerName);
    trigger.appendChild(chevron);

    var panel = document.createElement("div");
    panel.className = "vs-color-search__panel";
    panel.hidden = true;

    var searchRow = document.createElement("div");
    searchRow.className = "vs-color-search__search";
    searchRow.innerHTML =
      '<svg class="vs-color-search__search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">' +
      '<path fill="currentColor" d="M11.7 10.9a6 6 0 1 0-.8.8l3.2 3.2.8-.8-3.2-3.2zM7 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10z"/>' +
      "</svg>";

    var input = document.createElement("input");
    input.type = "search";
    input.className = "vs-color-search__input";
    input.placeholder = "Search by typing a keyword or using the ↑ ↓ keys";
    input.setAttribute("autocomplete", "off");
    input.setAttribute("autocapitalize", "off");
    input.setAttribute("spellcheck", "false");
    input.setAttribute("aria-label", "Search colors");
    searchRow.appendChild(input);

    var list = document.createElement("ul");
    list.className = "vs-color-search__list";
    list.setAttribute("role", "listbox");
    list.setAttribute("aria-label", "Colors");

    var optionEls = [];
    var activeIndex = -1;

    function setOpen(open) {
      root.setAttribute("data-open", open ? "true" : "false");
      trigger.setAttribute("aria-expanded", open ? "true" : "false");
      panel.hidden = !open;
      if (open) {
        input.value = "";
        filterOptions("");
        window.setTimeout(function () {
          input.focus();
        }, 0);
      } else {
        clearSwatchFilter(swatchRow);
        setActiveOption(-1);
      }
    }

    function setActiveOption(index) {
      activeIndex = index;
      optionEls.forEach(function (el, i) {
        var on = i === index;
        el.classList.toggle("vs-color-search__option--active", on);
        el.setAttribute("aria-selected", on ? "true" : "false");
        if (on) {
          el.scrollIntoView({ block: "nearest" });
        }
      });
    }

    function visibleOptions() {
      return optionEls.filter(function (el) {
        return !el.hidden;
      });
    }

    function filterOptions(query) {
      var firstVisible = -1;
      optionEls.forEach(function (el, i) {
        var label = el.getAttribute("data-vs-color") || "";
        var match = colorMatchesQuery(label, query);
        el.hidden = !match;
        if (match && firstVisible < 0) firstVisible = i;
      });
      syncSwatchFilter(swatchRow, query);
      setActiveOption(firstVisible);
    }

    function selectProduct(product) {
      if (!product || !product.handle) return;
      if (product.active) {
        setOpen(false);
        var swatch = swatchRow.querySelector(
          '.vs-swatch[data-vs-handle="' + product.handle + '"]'
        );
        if (swatch) {
          clearSwatchFilter(swatchRow);
          swatch.classList.add("vs-swatch--search-hit");
          swatch.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
        return;
      }
      window.location.href = "/products/" + product.handle;
    }

    products.forEach(function (product, index) {
      var li = document.createElement("li");
      li.className =
        "vs-color-search__option" + (product.active ? " vs-color-search__option--current" : "");
      li.setAttribute("role", "option");
      li.setAttribute("data-vs-color", getColorLabel(product));
      li.setAttribute("data-vs-handle", product.handle || "");
      li.setAttribute("aria-selected", product.active ? "true" : "false");
      if (product.active) activeIndex = index;

      var swatch = document.createElement("span");
      swatch.className = "vs-color-search__swatch";
      var dot = document.createElement("span");
      dot.className = "vs-color-search__dot";
      dot.style.backgroundColor = product.color_hex || "#ccc";
      swatch.appendChild(dot);

      var name = document.createElement("span");
      name.className = "vs-color-search__option-name";
      name.textContent = getColorLabel(product);

      li.appendChild(swatch);
      li.appendChild(name);
      li.addEventListener("mousedown", stopEvent);
      li.addEventListener("click", function (e) {
        stopEvent(e);
        selectProduct(product);
      });
      list.appendChild(li);
      optionEls.push(li);
    });

    panel.appendChild(searchRow);
    panel.appendChild(list);
    root.appendChild(trigger);
    root.appendChild(panel);

    trigger.addEventListener("click", function (e) {
      stopEvent(e);
      setOpen(root.getAttribute("data-open") !== "true");
    });

    input.addEventListener("input", function () {
      filterOptions(input.value);
    });

    input.addEventListener("keydown", function (e) {
      var visible = visibleOptions();
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        trigger.focus();
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        if (!visible.length) return;
        var currentVisibleIndex = visible.indexOf(optionEls[activeIndex]);
        if (currentVisibleIndex < 0) currentVisibleIndex = e.key === "ArrowDown" ? -1 : 0;
        var next =
          e.key === "ArrowDown"
            ? (currentVisibleIndex + 1) % visible.length
            : (currentVisibleIndex - 1 + visible.length) % visible.length;
        setActiveOption(optionEls.indexOf(visible[next]));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        var current = optionEls[activeIndex];
        if (current && !current.hidden) {
          var handle = current.getAttribute("data-vs-handle");
          var product = products.filter(function (p) {
            return p.handle === handle;
          })[0];
          selectProduct(product);
        }
      }
    });

    document.addEventListener("click", function onDocClick(e) {
      if (!root.isConnected) {
        document.removeEventListener("click", onDocClick);
        return;
      }
      if (root.getAttribute("data-open") !== "true") return;
      if (root.contains(e.target)) return;
      setOpen(false);
    });

    return root;
  }

  // --- PDP ---

  function buildPdpSwatch(product) {
    var link = document.createElement("a");
    link.href = "/products/" + product.handle;
    link.className = "vs-swatch" + (product.active ? " vs-swatch--active" : "");
    link.title = product.option_value;
    link.setAttribute("aria-label", product.option_value);
    link.setAttribute("data-vs-color", getColorLabel(product));
    if (product.handle) link.setAttribute("data-vs-handle", product.handle);
    if (product.active) {
      link.setAttribute("aria-current", "true");
      link.addEventListener("click", stopEvent);
    }
    link.appendChild(buildDot(product));
    return link;
  }

  function isMaterialActive(item, materials) {
    if (item.active) return true;
    if (materials && materials.current && item.name) {
      return String(item.name).toLowerCase() === String(materials.current).toLowerCase();
    }
    return false;
  }

  function buildPdpMaterialSwatch(item, materials) {
    var active = isMaterialActive(item, materials);
    var link = document.createElement("a");
    link.href = "/products/" + item.handle;
    link.className = "vs-material" + (active ? " vs-material--active" : "");
    link.title = item.name || "";
    link.setAttribute("aria-label", item.name || "Fabric");
    if (active) {
      link.setAttribute("aria-current", "true");
      link.addEventListener("click", stopEvent);
    }

    var imgUrl = (item.image_urls && item.image_urls[0]) || "";
    if (imgUrl) {
      var img = document.createElement("img");
      img.src = withImageWidth(imgUrl, 160);
      img.alt = item.name || "";
      img.width = 40;
      img.height = 40;
      img.loading = "lazy";
      link.appendChild(img);
    } else {
      var fallback = document.createElement("span");
      fallback.className = "vs-material__fallback";
      fallback.textContent = (item.name || "?").charAt(0);
      link.appendChild(fallback);
    }
    return link;
  }

  function buildMaterialsGroup(materials) {
    var items = getMaterialItems(materials);
    var wrapper = document.createElement("div");
    wrapper.className = "vs-material-group";
    wrapper.setAttribute("role", "group");
    wrapper.setAttribute("aria-label", "Fabric options");

    var head = document.createElement("div");
    head.className = "vs-material-group__head";
    var label = document.createElement("span");
    label.className = "vs-material-group__label";
    label.textContent = "Fabric: ";
    var current = document.createElement("strong");
    current.className = "vs-material-group__current";
    current.textContent = materials.current || "";
    head.appendChild(label);
    head.appendChild(current);
    wrapper.appendChild(head);

    var row = document.createElement("div");
    row.className = "vs-material-group__items";
    items.forEach(function (item) {
      row.appendChild(buildPdpMaterialSwatch(item, materials));
    });
    wrapper.appendChild(row);
    return wrapper;
  }

  function getHeightItems(heights) {
    if (!heights || !heights.items || !heights.items.length) return [];
    return heights.items;
  }

  function isHeightActive(item, heights) {
    if (item.active) return true;
    if (heights && heights.current && item.name) {
      return (
        String(item.name).toLowerCase() === String(heights.current).toLowerCase()
      );
    }
    return false;
  }

  function getHeightItemDesc(item) {
    return item.desc || item.description || item.subtitle || "";
  }

  function buildPdpHeightSwatch(item, heights) {
    var active = isHeightActive(item, heights);
    var itemDesc = getHeightItemDesc(item);
    var link = document.createElement("a");
    link.href = "/products/" + item.handle;
    link.className = "vs-height" + (active ? " vs-height--active" : "");
    if (itemDesc) link.className += " vs-height--has-desc";
    var labelText = item.name || getHeightOptionLabel(heights);
    if (itemDesc) labelText += " — " + itemDesc;
    link.title = labelText;
    link.setAttribute("role", "radio");
    link.setAttribute("aria-checked", active ? "true" : "false");
    link.setAttribute("aria-label", labelText);
    if (active) {
      link.setAttribute("aria-current", "page");
      link.addEventListener("click", stopEvent);
    }

    var name = document.createElement("span");
    name.className = "vs-height__label";
    name.textContent = item.name || "";
    link.appendChild(name);

    if (itemDesc) {
      var desc = document.createElement("span");
      desc.className = "vs-height__desc";
      desc.textContent = itemDesc;
      link.appendChild(desc);
    }
    return link;
  }

  function getHeightOptionLabel(heights) {
    var label = heights && heights.label != null ? String(heights.label) : "";
    label = label.trim();
    return label || "Height";
  }

  function buildHeightsGroup(heights) {
    var items = getHeightItems(heights);
    var optionLabel = getHeightOptionLabel(heights);
    var wrapper = document.createElement("div");
    wrapper.className =
      "vs-height-group vs-height-group--boxes vs-height-group--count-" +
      Math.min(items.length, 5);
    wrapper.setAttribute("role", "group");
    wrapper.setAttribute("aria-label", optionLabel + " options");

    var head = document.createElement("div");
    head.className = "vs-height-group__head";
    var label = document.createElement("span");
    label.className = "vs-height-group__label";
    label.textContent = optionLabel + ":";
    var current = document.createElement("span");
    current.className = "vs-height-group__current";
    current.textContent = " " + (heights.current || "");
    head.appendChild(label);
    head.appendChild(current);
    wrapper.appendChild(head);

    var track = document.createElement("div");
    track.className = "vs-height-group__track";
    track.setAttribute("role", "radiogroup");
    track.setAttribute("aria-label", "Select " + optionLabel.toLowerCase());
    items.forEach(function (item) {
      track.appendChild(buildPdpHeightSwatch(item, heights));
    });
    wrapper.appendChild(track);
    return wrapper;
  }

  function hideNativeColorOptionWhenSwatches() {
    var root = qs("#variant-swatches-root");
    if (!root) return;
    var shouldHide = root.querySelectorAll(".vs-swatch").length > 1;
    document
      .querySelectorAll(".wt-product__main .wt-product__options .wt-product__option")
      .forEach(function (opt) {
        var label = opt.querySelector(".wt-product__option__title .label");
        if (!label) return;
        var name = String(label.textContent || "")
          .replace(/:[\s\S]*$/, "")
          .trim()
          .toLowerCase();
        if (name !== "color" && name !== "colour") return;
        if (shouldHide) {
          opt.setAttribute("hidden", "");
          opt.style.display = "none";
          opt.setAttribute("data-vs-color-hidden", "true");
        } else if (opt.getAttribute("data-vs-color-hidden") === "true") {
          opt.removeAttribute("hidden");
          opt.style.display = "";
          opt.removeAttribute("data-vs-color-hidden");
        }
      });
  }

  function renderPdp(mountEl, payload) {
    var group = normalizeGroup(payload);
    var heights = payload && payload.heights;
    var materials = payload && payload.materials;
    var products = (group && group.products) || [];
    var heightItems = getHeightItems(heights);
    var materialItems = getMaterialItems(materials);

    mountEl.innerHTML = "";

    if (heightItems.length > 1) {
      mountEl.appendChild(buildHeightsGroup(heights));
    }

    if (materialItems.length > 1) {
      mountEl.appendChild(buildMaterialsGroup(materials));
    }

    if (products.length) {
      var parts = createWrapper(group);
      if (products.length > 1) {
        parts.wrapper.insertBefore(buildColorSearch(products, parts.swatchRow), parts.swatchRow);
        parts.wrapper.classList.add("vs-swatch-group--with-search");
      }
      products.forEach(function (product) {
        parts.swatchRow.appendChild(buildPdpSwatch(product));
      });
      mountEl.appendChild(parts.wrapper);
    }

    hideNativeColorOptionWhenSwatches();
  }

  function isMountRendered(mount) {
    return mount.dataset.vsInitialized === "true" &&
      (mount.querySelector(".vs-swatch-group") ||
        mount.querySelector(".vs-material-group") ||
        mount.querySelector(".vs-height-group"));
  }

  function resetStuckMount(mount) {
    var rendered = mount.querySelector(".vs-swatch-group");
    if (!rendered && mount.dataset.vsInitialized !== "false") {
      mount.dataset.vsInitialized = "false";
    }
  }

  function initProduct() {
    var root = qs("#variant-swatches-root");
    if (!root) return;

    resetStuckMount(root);
    if (isMountRendered(root)) {
      hideNativeColorOptionWhenSwatches();
      return;
    }
    if (root.dataset.vsInitialized === "pending") return;

    var cfg = getMountConfig(root);
    if (!cfg.productId || !cfg.apiBase || !cfg.shop) return;

    root.dataset.vsInitialized = "pending";

    fetchVariantGroupsRaw(cfg)
      .then(function (payload) {
        if (!payload) return;
        renderPdp(root, payload);
        root.dataset.vsInitialized = "true";
      })
      .catch(function (err) {
        root.dataset.vsInitialized = "false";
        if (window.console) console.error("[variant-swatches]", err);
      });
  }

  // --- Collection cards ---

  function withImageWidth(url, width) {
    if (!url) return url;
    if (/[?&]width=/.test(url)) return url;
    return url + (url.indexOf("?") === -1 ? "?" : "&") + "width=" + width;
  }

  var plusImagePairCache =
    window.__variantSwatchesPlusImageCache ||
    (window.__variantSwatchesPlusImageCache = {});

  function plusAltKind(alt) {
    var value = String(alt || "").toLowerCase();
    if (!value) return "";
    var isPlus =
      value.indexOf("[sizeplus]") !== -1 ||
      value.indexOf("[plus-size]") !== -1 ||
      value.indexOf("[plus_size]") !== -1 ||
      value.indexOf("[plussize]") !== -1 ||
      value.indexOf("[plus-front]") !== -1 ||
      value.indexOf("[plus-back]") !== -1 ||
      value.indexOf("[sizeplus-front]") !== -1 ||
      value.indexOf("[sizeplus-back]") !== -1 ||
      value.indexOf("[plus]") !== -1 ||
      value.indexOf("#sizeplus") !== -1 ||
      value.indexOf("#plus-front") !== -1 ||
      value.indexOf("#plus-back") !== -1 ||
      value.indexOf("#plus") !== -1;
    if (!isPlus) return "";
    if (
      value.indexOf("[back]") !== -1 ||
      value.indexOf("[plus-back]") !== -1 ||
      value.indexOf("[sizeplus-back]") !== -1 ||
      value.indexOf("#plus-back") !== -1 ||
      value.indexOf("背图") !== -1 ||
      value.indexOf("背面") !== -1
    ) {
      return "back";
    }
    if (
      value.indexOf("[front]") !== -1 ||
      value.indexOf("[plus-front]") !== -1 ||
      value.indexOf("[sizeplus-front]") !== -1 ||
      value.indexOf("#plus-front") !== -1 ||
      value.indexOf("正图") !== -1 ||
      value.indexOf("正面") !== -1
    ) {
      return "front";
    }
    return "plus";
  }

  function mediaPreviewUrl(media) {
    if (!media) return "";
    if (media.preview_image && media.preview_image.src) return media.preview_image.src;
    if (media.src) return media.src;
    return "";
  }

  function pickPlusImagePair(mediaItems) {
    var front = "";
    var back = "";
    var unmarked = [];
    (mediaItems || []).forEach(function (media) {
      var kind = plusAltKind((media && media.alt) || "") ||
        plusAltKind((media && media.preview_image && media.preview_image.alt) || "");
      var url = mediaPreviewUrl(media);
      if (!kind || !url) return;
      if (kind === "back") {
        if (!back) back = url;
      } else if (kind === "front") {
        if (!front) front = url;
      } else {
        unmarked.push(url);
      }
    });
    unmarked.forEach(function (url) {
      if (!front) front = url;
      else if (!back) back = url;
    });
    return { front: front || back, back: back };
  }

  function imageListUrls(payload) {
    var urls = [];
    if (payload && payload.images && payload.images.length) {
      payload.images.forEach(function (item) {
        if (typeof item === "string" && item) urls.push(item);
        else if (item && item.src) urls.push(item.src);
      });
      return urls;
    }
    (payload && payload.media ? payload.media : []).forEach(function (media) {
      if (media && media.media_type && media.media_type !== "image") return;
      var url = mediaPreviewUrl(media);
      if (url) urls.push(url);
    });
    return urls;
  }

  function positionalPlusPair(payload, storekitUrls) {
    var urls = imageListUrls(payload);
    if (!urls.length && storekitUrls && storekitUrls.length) urls = storekitUrls;
    return {
      front: urls[2] || urls[0] || "",
      back: urls[3] || urls[1] || "",
    };
  }

  function resolvePlusImagePair(product) {
    var handle = product && product.handle;
    var storekitUrls = (product && product.image_urls) || [];
    var positional = positionalPlusPair({ images: storekitUrls }, storekitUrls);
    if (!handle) {
      return Promise.resolve(positional);
    }
    if (plusImagePairCache[handle]) return plusImagePairCache[handle];

    plusImagePairCache[handle] = fetch("/products/" + encodeURIComponent(handle) + ".js", {
      credentials: "same-origin",
    })
      .then(function (res) {
        if (!res.ok) throw new Error("product.js " + res.status);
        return res.json();
      })
      .then(function (payload) {
        var tagged = pickPlusImagePair(payload.media || []);
        var byIndex = positionalPlusPair(payload, storekitUrls);
        return {
          front: tagged.front || byIndex.front,
          back: tagged.back || byIndex.back,
        };
      })
      .catch(function () {
        delete plusImagePairCache[handle];
        return positional;
      });

    return plusImagePairCache[handle];
  }

  function isPreferPlusMount(mount) {
    var swatch = mount && mount.closest && mount.closest("color-swatch");
    return !!(swatch && swatch.getAttribute("data-prefer-plus-images") === "true");
  }

  function seedPlusCacheFromCard(mount) {
    var swatch = mount && mount.closest && mount.closest("color-swatch");
    if (!swatch) return;
    var handle = swatch.getAttribute("data-plus-handle") || swatch.getAttribute("data-current-variant-handle");
    var front = swatch.getAttribute("data-plus-front") || "";
    var back = swatch.getAttribute("data-plus-back") || "";
    if (handle && front && !plusImagePairCache[handle]) {
      plusImagePairCache[handle] = Promise.resolve({ front: front, back: back });
    }
  }

  function ensureCardHoverImg(cardCtx) {
    if (!cardCtx || cardCtx.hoverImg || !cardCtx.img || !cardCtx.img.parentNode) return;
    var hover = cardCtx.img.cloneNode(true);
    hover.classList.add("card__img--hover");
    hover.classList.remove("hidden");
    hover.removeAttribute("srcset");
    cardCtx.img.parentNode.appendChild(hover);
    cardCtx.hoverImg = hover;
  }

  function getCardContext(mount) {
    var colorSwatch = mount.closest("color-swatch");
    if (!colorSwatch) return null;

    var container = colorSwatch.querySelector(".card__container");
    if (!container) return null;

    var imgs = container.querySelectorAll(".card__img");
    return {
      pictureLink: container.querySelector(".card__picture-container a.card"),
      titleLink: container.querySelector(".card__title a"),
      img: imgs[0] || null,
      hoverImg: imgs[1] || null,
      loader: container.querySelector(".card__loader"),
      video: container.querySelector("video"),
      quickAdds: colorSwatch.querySelectorAll("quick-add"),
      preferPlusImages: colorSwatch.getAttribute("data-prefer-plus-images") === "true",
      colorSwatch: colorSwatch,
    };
  }

  function showCardLoader(cardCtx) {
    if (!cardCtx || !cardCtx.loader || !cardCtx.img) return;
    cardCtx.loader.classList.remove("hidden");
    cardCtx.loader.innerHTML = '<div class="spinner-ring"></div>';
    cardCtx.img.style.opacity = "0";
  }

  function hideCardLoader(cardCtx) {
    if (!cardCtx || !cardCtx.loader || !cardCtx.img) return;
    cardCtx.loader.classList.add("hidden");
    cardCtx.loader.innerHTML = "";
    cardCtx.img.style.opacity = "1";
  }

  function setAttributes(element, attrs) {
    if (!element) return;
    Object.keys(attrs).forEach(function (key) {
      element.setAttribute(key, attrs[key]);
    });
  }

  function syncMountProductIdentity(mount, product) {
    if (!mount || !product) return;

    if (product.product_id) {
      mount.setAttribute("data-product-id", String(product.product_id));
    }

    var colorSwatch = mount.closest("color-swatch");
    if (!colorSwatch) return;

    if (product.handle) {
      colorSwatch.setAttribute("data-current-variant-handle", product.handle);
    }
    if (product.product_id) {
      colorSwatch.setAttribute("data-current-product-id", String(product.product_id));
    }
  }

  function asGid(type, id) {
    if (id == null || id === "") return null;
    var s = String(id);
    if (s.indexOf("gid://") === 0) return s;
    return "gid://shopify/" + type + "/" + s;
  }

  /** 色卡切换后同步 Snowplow product_clicked props，避免点到旧商品 */
  function syncCardTrackProps(cardCtx, product) {
    if (!cardCtx || !product) return;
    var links = [cardCtx.pictureLink, cardCtx.titleLink];
    var imageUrls = product.image_urls || [];
    var href = product.handle ? "/products/" + product.handle : null;

    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      if (!link || !link.getAttribute) continue;
      if (link.getAttribute("data-track") !== "product_clicked") continue;

      var props = {};
      try {
        props = JSON.parse(link.getAttribute("data-track-props") || "{}") || {};
      } catch (e) {
        props = {};
      }

      if (product.product_id) props.item_id = asGid("Product", product.product_id);
      if (product.title) props.item_name = product.title;
      if (product.vendor) props.item_brand = product.vendor;
      if (product.product_type) props.item_category = product.product_type;
      if (product.price != null && product.price !== "") props.price = Number(product.price);
      if (href) props.product_url = href;
      if (imageUrls[0]) props.image_url = String(imageUrls[0]).slice(0, 255);
      if (product.option_value) props.item_variant = product.option_value;
      if (product.variant_id) props.variant_id = asGid("ProductVariant", product.variant_id);
      if (product.sku != null) props.sku = product.sku;

      link.setAttribute("data-track-props", JSON.stringify(props));
    }
  }

  function applyCardImages(cardCtx, mainImg, hoverImg) {
    if (!mainImg || !cardCtx.img) return;

    showCardLoader(cardCtx);
    cardCtx.img.classList.remove("hidden");
    cardCtx.img.onload = function () {
      hideCardLoader(cardCtx);
      cardCtx.img.onload = null;
    };

    setAttributes(cardCtx.img, { src: mainImg });
    cardCtx.img.removeAttribute("srcset");

    if (hoverImg) {
      ensureCardHoverImg(cardCtx);
      if (cardCtx.hoverImg) {
        setAttributes(cardCtx.hoverImg, { src: hoverImg });
        cardCtx.hoverImg.removeAttribute("srcset");
        cardCtx.hoverImg.classList.remove("hidden");
      }
    } else if (cardCtx.hoverImg) {
      cardCtx.hoverImg.classList.add("hidden");
    }

    if (cardCtx.video) {
      cardCtx.video.classList.add("hidden");
    }
  }

  function applyCardProduct(cardCtx, product, mount) {
    if (!cardCtx || !product) return;

    var href = "/products/" + product.handle;
    var imageUrls = product.image_urls || [];

    setAttributes(cardCtx.pictureLink, { href: href });
    setAttributes(cardCtx.titleLink, { href: href });
    syncCardTrackProps(cardCtx, product);

    if (cardCtx.titleLink && product.title) {
      cardCtx.titleLink.textContent = product.title;
    }

    Array.prototype.forEach.call(cardCtx.quickAdds || [], function (quickAdd) {
      quickAdd.setAttribute("data-product-handle", product.handle);
      var btn = quickAdd.querySelector("button");
      if (btn) btn.dataset.productUrl = href;
    });

    if (mount) {
      syncMountProductIdentity(mount, product);
    }

    if (cardCtx.preferPlusImages) {
      var swatch = cardCtx.colorSwatch;
      var reqId = Number(swatch && swatch.dataset.plusImgReq || 0) + 1;
      if (swatch) swatch.dataset.plusImgReq = String(reqId);
      showCardLoader(cardCtx);
      resolvePlusImagePair(product).then(function (pair) {
        if (swatch && swatch.dataset.plusImgReq !== String(reqId)) return;
        applyCardImages(
          cardCtx,
          withImageWidth(pair.front || "", IMAGE_WIDTH),
          withImageWidth(pair.back || "", IMAGE_WIDTH)
        );
      });
      return;
    }

    applyCardImages(
      cardCtx,
      withImageWidth(imageUrls[0] || "", IMAGE_WIDTH),
      withImageWidth(imageUrls[1] || "", IMAGE_WIDTH)
    );
  }

  function setActiveSwatch(swatchRow, activeEl) {
    swatchRow.querySelectorAll(".vs-swatch").forEach(function (swatch) {
      swatch.classList.remove("vs-swatch--active");
      swatch.removeAttribute("aria-current");
    });
    activeEl.classList.add("vs-swatch--active");
    activeEl.setAttribute("aria-current", "true");
  }

  function buildCardSwatch(product, mount, swatchRow, selectedKey) {
    var isActive = isProductSelected(product, selectedKey);
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "vs-swatch" + (isActive ? " vs-swatch--active" : "");
    btn.title = product.option_value;
    btn.setAttribute("aria-label", product.option_value);
    btn.dataset.vsProductKey = productKey(product);
    if (isActive) {
      btn.setAttribute("aria-current", "true");
    }
    btn.appendChild(buildDot(product));

    if (isPreferPlusMount(mount) && product.handle) {
      btn.addEventListener("pointerenter", function () {
        resolvePlusImagePair(product);
      });
    }

    btn.addEventListener("click", function (e) {
      stopEvent(e);
      mount.dataset.vsSelectedKey = productKey(product);
      applyCardProduct(getCardContext(mount), product, mount);
      setActiveSwatch(swatchRow, btn);
    });

    return btn;
  }

  function appendCardSwatches(container, products, mount, swatchRow, selectedKey) {
    products.forEach(function (product) {
      container.appendChild(buildCardSwatch(product, mount, swatchRow, selectedKey));
    });
  }

  function buildCollapseBtn(mount) {
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "vs-swatch-group__collapse";
    btn.setAttribute("aria-label", "Collapse color options");
    btn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" aria-hidden="true" focusable="false">' +
      '<path fill="currentColor" d="M6 3.8 2.2 7.6h7.6L6 3.8z"/>' +
      "</svg>";
    btn.addEventListener("mousedown", stopEvent);
    btn.addEventListener("click", function (e) {
      stopEvent(e);
      // 收起回首屏批量结果（当前+随机），不要用全量 position 顺序重画前几块
      renderCard(mount, mount._vsBatchGroup || mount._vsFullGroup);
    });
    return btn;
  }

  /** 已展示顺序在前，全量里未出现的追加在后（加载更多不打乱首屏） */
  function mergeBatchThenFull(batchGroup, fullGroup) {
    var shown = {};
    var merged = [];
    var batchProducts = (batchGroup && batchGroup.products) || [];
    var fullProducts = (fullGroup && fullGroup.products) || [];

    batchProducts.forEach(function (p) {
      var key = productKey(p);
      if (!key || shown[key]) return;
      shown[key] = true;
      merged.push(p);
    });
    fullProducts.forEach(function (p) {
      var key = productKey(p);
      if (!key || shown[key]) return;
      shown[key] = true;
      merged.push(p);
    });

    var base = fullGroup || batchGroup || {};
    return {
      group_id: base.group_id,
      name: base.name,
      product_count:
        base.product_count ||
        (batchGroup && batchGroup.product_count) ||
        merged.length,
      products: merged,
    };
  }

  function buildMoreBtn(mount, hiddenCount) {
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "vs-swatch-group__more";
    btn.textContent = "+" + hiddenCount;
    btn.setAttribute("aria-label", "Show " + hiddenCount + " more color options");
    btn.addEventListener("mousedown", stopEvent);
    btn.addEventListener("click", function (e) {
      stopEvent(e);
      if (btn.disabled) return;

      // 已展开过：直接用合并结果，避免再用全量顺序重绘
      if (mount._vsExpandedGroup) {
        renderCard(mount, mount._vsExpandedGroup, { expanded: true });
        return;
      }

      btn.disabled = true;
      var originalText = btn.textContent;
      btn.textContent = "...";

      fetchSingleGroup(getMountConfig(mount))
        .then(function (fullGroup) {
          if (!fullGroup) throw new Error("variant-groups response missing products");
          mount._vsFullGroup = fullGroup;
          var merged = mergeBatchThenFull(mount._vsBatchGroup, fullGroup);
          mount._vsExpandedGroup = merged;
          renderCard(mount, merged, { expanded: true });
        })
        .catch(function (err) {
          btn.disabled = false;
          btn.textContent = originalText;
          if (window.console) console.error("[variant-swatches]", err);
        });
    });
    return btn;
  }

  function renderCard(mount, group, options) {
    if (!group) return;

    var forceExpanded = options && options.expanded;
    var products = group.products || [];
    var totalCount = getTotalCount(group);

    ensureSelectedKey(mount, products);
    var selectedKey = mount.dataset.vsSelectedKey || "";

    if (forceExpanded) {
      mount._vsExpandedGroup = group;
      if (!mount._vsFullGroup) mount._vsFullGroup = group;
    } else if (!mount._vsBatchGroup) {
      mount._vsBatchGroup = group;
    }

    var parts = createWrapper(group);
    var collapsedLimit = getCollapsedLimit(mount);

    if (!forceExpanded && totalCount > collapsedLimit) {
      parts.wrapper.classList.add("vs-swatch-group--collapsed");
      var visible = pickVisibleProducts(products, collapsedLimit, selectedKey);
      var hiddenCount = Math.max(0, totalCount - collapsedLimit);

      appendCardSwatches(parts.swatchRow, visible, mount, parts.swatchRow, selectedKey);
      if (hiddenCount > 0) {
        parts.swatchRow.appendChild(buildMoreBtn(mount, hiddenCount));
      }
    } else {
      var orderedProducts = orderProductsWithSelectedFirst(products, selectedKey);
      appendCardSwatches(parts.swatchRow, orderedProducts, mount, parts.swatchRow, selectedKey);
      parts.wrapper.classList.add("vs-swatch-group--expanded");
      if (totalCount > collapsedLimit) {
        parts.swatchRow.appendChild(buildCollapseBtn(mount));
      }
    }

    mount.innerHTML = "";
    mount.appendChild(parts.wrapper);

    if (isPreferPlusMount(mount)) {
      seedPlusCacheFromCard(mount);
    }
  }

  function getGroupForProduct(groups, productId) {
    if (!groups || !productId) return null;
    return (
      groups[productId] ||
      groups[String(productId)] ||
      groups[Number(productId)] ||
      null
    );
  }

  function finishCardsBatch() {
    cardsInFlight = false;
    if (cardsPendingRescan) {
      cardsPendingRescan = false;
      scanCards();
    }
  }

  function scanCards() {
    if (isProductPage()) return;

    var mounts = Array.prototype.slice.call(
      document.querySelectorAll("[data-variant-swatches-root]")
    );
    var pending = mounts.filter(function (mount) {
      if (isMountRendered(mount)) return false;
      resetStuckMount(mount);
      return (
        mount.dataset.vsInitialized !== "true" &&
        mount.dataset.vsInitialized !== "pending"
      );
    });

    if (!pending.length) return;
    if (cardsInFlight) {
      cardsPendingRescan = true;
      return;
    }

    var cfg = getMountConfig(pending[0]);
    if (!cfg.apiBase || !cfg.shop) return;

    var productIds = [];
    pending.forEach(function (mount) {
      var productId = mount.getAttribute("data-product-id");
      if (!productId) return;
      if (productIds.indexOf(productId) === -1) productIds.push(productId);
      mount.dataset.vsInitialized = "pending";
    });

    if (!productIds.length) return;

    cardsInFlight = true;

    var batchKey = cacheKey(
      { apiBase: cfg.apiBase, shop: cfg.shop, productId: productIds.join(",") },
      "batch"
    );

    if (!requestCache[batchKey]) {
      var url =
        cfg.apiBase +
        "/variant-groups?shop=" +
        encodeURIComponent(cfg.shop) +
        "&productIds=" +
        encodeURIComponent(productIds.join(","));

      requestCache[batchKey] = fetch(url, { credentials: "omit" })
        .then(function (res) {
          if (!res.ok) throw new Error("variant-groups request failed: " + res.status);
          return res.json();
        })
        .catch(function (err) {
          delete requestCache[batchKey];
          throw err;
        });
    }

    requestCache[batchKey]
      .then(function (data) {
        var groups = (data && data.groups) || {};

        pending.forEach(function (mount) {
          var productId = mount.getAttribute("data-product-id");
          var group = normalizeGroup(getGroupForProduct(groups, productId));

          if (group) {
            renderCard(mount, group);
            mount.dataset.vsInitialized = "true";
            return;
          }

          mount.remove();
          mount.dataset.vsInitialized = "true";
        });
      })
      .catch(function (err) {
        pending.forEach(function (mount) {
          mount.dataset.vsInitialized = "false";
        });
        if (window.console) console.error("[variant-swatches]", err);
      })
      .finally(finishCardsBatch);
  }

  function observeProductGrid() {
    if (observeProductGrid._observed) return;
    observeProductGrid._observed = true;

    var grid = document.getElementById("product-grid");
    if (!grid || typeof MutationObserver === "undefined") return;

    var timer;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(scanCards, 80);
    }).observe(grid, { childList: true, subtree: true });
  }

  function bootstrap() {
    initProduct();
    scanCards();
    observeProductGrid();
  }

  window.__variantSwatches = {
    initProduct: initProduct,
    scanCards: scanCards,
  };
  window.VariantSwatches = window.__variantSwatches;
  window.VariantSwatchesCards = { scan: scanCards };

  // Start as early as possible on PDP; fallback on DOMContentLoaded.
  bootstrap();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrap, { once: true });
  }

  window.addEventListener("pageshow", bootstrap);
})();
